/**
 * PageTools — the small controls every page shares
 * ================================================
 * From the usability review of 23 October 2026:
 *
 *   item 4   FreshnessBadge   "Show the last saved figures right away with an
 *                             'updating…' badge"
 *   item 5   InfoTip          "Move [working notes] behind small info (ⓘ)
 *                             tooltips or into the Methodology tab"
 *   item 8   ExportCsvButton  "Add a CSV/Excel button next to each table"
 *            PdfButton        "and a 'Download PDF' for each page"
 *   item 15  LastRefreshed    "Add a 'Last refreshed' time next to the
 *                             Refresh All button"
 */
import React, { useEffect, useId, useRef, useState } from "react";
import { ago } from "./format";
import "./PageTools.css";

/* ── CSV ──────────────────────────────────────────────────────────────── */

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Rows to CSV text. A column is { label, value: row => raw value }. */
export function toCsv(columns, rows) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(","));
  return [header, ...body].join("\r\n");
}

export function downloadText(filename, text, type = "text/csv;charset=utf-8") {
  // The byte-order mark makes Excel read the file as UTF-8, so "Valuno Group
  // AB (publ)" and a krona sign survive the round trip.
  const blob = new Blob(["﻿", text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const datedFilename = (stem, ext = "csv") =>
  `${stem}-${new Date().toISOString().slice(0, 10)}.${ext}`;

/** Exports the rows a table is showing. Raw numbers, so Excel can add them up. */
export function ExportCsvButton({ filename, columns, rows, label = "Export CSV" }) {
  const disabled = !rows || rows.length === 0;
  return (
    <button
      type="button"
      className="pt-button no-print"
      disabled={disabled}
      title={disabled ? "Nothing to export" : `Download these ${rows.length} rows as a CSV file (opens in Excel)`}
      onClick={() => downloadText(datedFilename(filename), toCsv(columns, rows))}
    >
      <span aria-hidden="true">⤓</span> {label}
    </button>
  );
}

/** The page as a PDF, through the browser's own print-to-PDF. */
export function PdfButton({ label = "Download PDF" }) {
  return (
    <button
      type="button"
      className="pt-button no-print"
      title="Opens the print dialog with the navigation hidden. Choose “Save as PDF”."
      onClick={() => window.print()}
    >
      <span aria-hidden="true">⎙</span> {label}
    </button>
  );
}

/* ── Freshness ────────────────────────────────────────────────────────── */

// Re-render once a minute so "2 min ago" keeps counting.
function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** "Updated 2 min ago" or "Updating… showing figures from 10:15". */
export function FreshnessBadge({ savedAt, refreshing, error, onRefresh }) {
  const now = useNow();
  if (!savedAt && !refreshing && !error) return null;
  return (
    <span className={`pt-fresh${refreshing ? " pt-fresh-busy" : ""}${error ? " pt-fresh-error" : ""}`} role="status">
      {refreshing ? (
        <>
          <span className="pt-dot" aria-hidden="true" />
          {savedAt ? `Updating… showing figures from ${ago(savedAt, now)}` : "Loading…"}
        </>
      ) : error ? (
        <>Could not refresh{savedAt ? ` · showing figures from ${ago(savedAt, now)}` : ""}</>
      ) : (
        <>Updated {ago(savedAt, now)}</>
      )}
      {onRefresh && !refreshing && (
        <button type="button" className="pt-link no-print" onClick={onRefresh}>Refresh</button>
      )}
    </span>
  );
}

/* ── Info tip ─────────────────────────────────────────────────────────── */

/**
 * A small ⓘ that opens a note on hover, focus or click. Working notes and
 * methodology go here rather than in a paragraph under every chart.
 */
export function InfoTip({ children, label = "How this is worked out", align = "left", wide = false }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <span
      className="pt-info"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="pt-info-button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ⓘ
      </button>
      {open && (
        <span id={id} role="tooltip" className={`pt-info-pop pt-info-${align}${wide ? " pt-info-wide" : ""}`}>
          {children}
        </span>
      )}
    </span>
  );
}

/** A heading row's right-hand cluster: freshness, export and PDF together. */
export function ToolRow({ children }) {
  return <div className="pt-row">{children}</div>;
}
